import React, { useState, useEffect } from "react";
import { Users, Loader2, Download, Phone, Mail, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OrganizerClientsTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("tika_organizer_token");
      const res = await axios.get(`${API}/organizer/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data || []);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const rows = [["Nom", "Email", "Téléphone", "Événement", "Tarif", "Quantité", "Total", "Statut", "Date"]];
    filteredOrders.forEach(o => {
      (o.items || []).forEach(item => {
        rows.push([
          o.customer_name, o.customer_email, o.customer_phone || "",
          item.event_title, item.ticket_type, item.quantity,
          o.total?.toFixed(2) || "0", o.payment_status || o.status || "",
          o.created_at?.slice(0, 10) || ""
        ]);
      });
    });
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clients_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV terminé");
  };

  // Get unique events from orders
  const eventList = [...new Map(
    orders.flatMap(o => (o.items || []).map(i => [i.event_id, i.event_title]))
  ).entries()].map(([id, title]) => ({ id, title }));

  const filteredOrders = orders.filter(o => {
    const matchesEvent = !selectedEvent || (o.items || []).some(i => i.event_id === selectedEvent);
    const matchesSearch = !search || 
      o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      o.customer_email?.toLowerCase().includes(search.toLowerCase()) ||
      o.customer_phone?.includes(search);
    return matchesEvent && matchesSearch;
  });

  // Unique clients count
  const uniqueEmails = new Set(filteredOrders.map(o => o.customer_email)).size;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span className="text-muted-foreground">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="organizer-clients-tab">
      {/* KPI */}
      <div className="grid grid-cols-3 gap-3">
        <div className="glass-card rounded-lg p-3 text-center">
          <div className="text-xl font-bold">{uniqueEmails}</div>
          <div className="text-xs text-muted-foreground">Clients</div>
        </div>
        <div className="glass-card rounded-lg p-3 text-center">
          <div className="text-xl font-bold">{filteredOrders.length}</div>
          <div className="text-xs text-muted-foreground">Commandes</div>
        </div>
        <div className="glass-card rounded-lg p-3 text-center">
          <div className="text-xl font-bold">{filteredOrders.reduce((s, o) => s + (o.total || 0), 0).toFixed(0)}€</div>
          <div className="text-xs text-muted-foreground">Revenus</div>
        </div>
      </div>

      {/* Event Filter */}
      {eventList.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <span
            className={`text-xs px-3 py-1.5 rounded-full cursor-pointer transition-colors ${!selectedEvent ? 'bg-primary/20 text-primary font-medium' : 'bg-muted/30 hover:bg-muted/50'}`}
            onClick={() => setSelectedEvent(null)}
          >
            Tous
          </span>
          {eventList.map(ev => (
            <span
              key={ev.id}
              className={`text-xs px-3 py-1.5 rounded-full cursor-pointer transition-colors truncate max-w-[200px] ${selectedEvent === ev.id ? 'bg-primary/20 text-primary font-medium' : 'bg-muted/30 hover:bg-muted/50'}`}
              onClick={() => setSelectedEvent(ev.id)}
            >
              {ev.title}
            </span>
          ))}
        </div>
      )}

      {/* Search + Export */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-clients-csv">
          <Download className="w-3 h-3 mr-1" />
          CSV
        </Button>
      </div>

      {/* Client List */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Aucune commande {selectedEvent ? "pour cet événement" : ""}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredOrders.map((order) => (
            <div key={order.id} className="glass-card rounded-lg p-3" data-testid={`client-order-${order.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                  <h4 className="font-semibold text-sm truncate">{order.customer_name}</h4>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      <a href={`mailto:${order.customer_email}`} className="hover:underline">{order.customer_email}</a>
                    </span>
                    {order.customer_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        <a href={`tel:${order.customer_phone}`} className="hover:underline">{order.customer_phone}</a>
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-bold text-sm">{(order.total || 0).toFixed(2)}€</div>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    order.payment_status === "paid" || order.payment_status === "completed"
                      ? "bg-green-500/20 text-green-400"
                      : "bg-yellow-500/20 text-yellow-400"
                  }`}>
                    {order.payment_status || order.status || "pending"}
                  </span>
                </div>
              </div>
              <div className="space-y-0.5">
                {(order.items || []).map((item, idx) => (
                  <div key={idx} className="text-xs text-muted-foreground flex justify-between">
                    <span>{item.event_title}</span>
                    <span>{item.ticket_type} × {item.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
